import React, { useState } from 'react';
import {
    Box,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Badge,
    Text,
    Spinner,
    Center,
    Alert,
    AlertIcon,
    AlertDescription,
    HStack,
    VStack,
    Flex,
    Image,
    IconButton,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import percentIcon from '../../../assets/icons/percentage_icon.png';
import type { OdooVsSaktiDivisionGroup } from '../types';

interface OdooVsSaktiTableProps {
    groupedData: OdooVsSaktiDivisionGroup[];
    loading: boolean;
    error: string | null;
}

const formatNumber = (num: number, decimals = 2) => {
    if (num === 0 || num === null || num === undefined) return '-';
    return new Intl.NumberFormat('id-ID', {
        minimumFractionDigits: 0,
        maximumFractionDigits: decimals,
    }).format(num);
};

const formatPercent = (ratio: number) => `${(ratio * 100).toFixed(1)}%`;

const getAccColorScheme = (ratio: number) => {
    if (ratio >= 0.8) return 'green';
    if (ratio >= 0.5) return 'yellow';
    return 'red';
};

const AccBadge: React.FC<{ ratio: number }> = ({ ratio }) => (
    <Badge colorScheme={getAccColorScheme(ratio)} variant="subtle" fontSize="xs">
        {formatPercent(ratio)}
    </Badge>
);

const accBg = (ratio: number) => {
    if (ratio >= 0.8) return 'green.100';
    if (ratio >= 0.5) return 'yellow.100';
    return 'red.100';
};

const HeaderInfoBox: React.FC<{ label: string; value: string; bg?: string }> = ({
    label,
    value,
    bg = '#ededed',
}) => (
    <HStack spacing={4}>
        <Box
            bg={bg}
            p={2}
            borderRadius="lg"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
        >
            <Image src={percentIcon} alt="%" boxSize="25px" objectFit="contain" />
        </Box>
        <Box>
            <Text color="black" fontSize="sm">{label}</Text>
            <Text color="black" fontWeight="bold">{value}</Text>
        </Box>
    </HStack>
);

export const OdooVsSaktiTable: React.FC<OdooVsSaktiTableProps> = ({
    groupedData,
    loading,
    error,
}) => {
    const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

    const toggleRow = (key: string) => {
        setExpandedRows((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    if (loading) {
        return (
            <Center py={10}>
                <Spinner size="xl" color="green.500" thickness="4px" />
            </Center>
        );
    }

    if (error) {
        return (
            <Alert status="error" borderRadius="md">
                <AlertIcon />
                <AlertDescription>{error}</AlertDescription>
            </Alert>
        );
    }

    if (!groupedData || groupedData.length === 0) {
        return (
            <Alert status="info" borderRadius="md">
                <AlertIcon />
                <AlertDescription>Tidak ada data untuk ditampilkan</AlertDescription>
            </Alert>
        );
    }

    return (
        <VStack spacing={6} align="stretch">
            {groupedData.map((division) => (
                <Box key={division.division_id}>
                    {/* Division header card */}
                    <Box borderRadius="lg" boxShadow="sm" mb={0} overflow="hidden" bg="white">
                        <Flex direction={{ base: 'column', lg: 'row' }} align="stretch">
                            <Box bg="#e7efdf" p={4} flex="2.5">
                                <Text color="black" fontWeight="bold">
                                    DIVISI | {division.division_name}
                                </Text>
                                <Text color="black" fontSize="sm">{division.estate_name}</Text>
                            </Box>

                            <Box bg="white" p={4} flex="1">
                                <HeaderInfoBox
                                    label="Akurasi HA"
                                    value={formatPercent(division.ha_acc)}
                                    bg={accBg(division.ha_acc)}
                                />
                            </Box>

                            <Box bg="white" p={4} flex="1">
                                <HeaderInfoBox
                                    label="Akurasi JJG"
                                    value={formatPercent(division.jjg_acc)}
                                    bg={accBg(division.jjg_acc)}
                                />
                            </Box>

                            <Box bg="white" p={4} flex="1">
                                <HeaderInfoBox
                                    label="Akurasi BRD"
                                    value={formatPercent(division.brd_acc)}
                                    bg={accBg(division.brd_acc)}
                                />
                            </Box>

                            <Box bg="white" p={4} flex="1">
                                <HeaderInfoBox
                                    label="Rata-rata Akurasi"
                                    value={formatPercent(division.overall_acc)}
                                    bg={accBg(division.overall_acc)}
                                />
                            </Box>
                        </Flex>
                    </Box>

                    {/* Data table */}
                    <Box overflowX="auto" bg="white" borderRadius="lg" boxShadow="sm" mt={3}>
                        <Table variant="unstyled" size="sm">
                            <Thead>
                                {/* Level 1 header */}
                                <Tr bg="#4A5A33">
                                    <Th colSpan={4} py={2} px={3}>
                                        <Text
                                            whiteSpace="nowrap"
                                            color="white"
                                            fontSize="2xs"
                                            fontWeight="normal"
                                            textAlign="center"
                                        >
                                            Informasi Pemanen
                                        </Text>
                                    </Th>
                                    <Th
                                        colSpan={3}
                                        borderLeft="1px solid"
                                        borderColor="whiteAlpha.300"
                                        borderBottom="none"
                                    >
                                        <Text
                                            whiteSpace="nowrap"
                                            color="white"
                                            fontSize="2xs"
                                            fontWeight="normal"
                                            textAlign="center"
                                        >
                                            Luas (Ha)
                                        </Text>
                                    </Th>
                                    <Th
                                        colSpan={3}
                                        borderLeft="1px solid"
                                        borderColor="whiteAlpha.300"
                                        borderBottom="none"
                                    >
                                        <Text
                                            whiteSpace="nowrap"
                                            color="white"
                                            fontSize="2xs"
                                            fontWeight="normal"
                                            textAlign="center"
                                        >
                                            Janjang (JJG)
                                        </Text>
                                    </Th>
                                    <Th
                                        colSpan={3}
                                        borderLeft="1px solid"
                                        borderColor="whiteAlpha.300"
                                        borderBottom="none"
                                    >
                                        <Text
                                            whiteSpace="nowrap"
                                            color="white"
                                            fontSize="2xs"
                                            fontWeight="normal"
                                            textAlign="center"
                                        >
                                            Brondolan (BRD)
                                        </Text>
                                    </Th>
                                </Tr>

                                {/* Level 2 header */}
                                <Tr bg="#e7efdf">
                                    <Th whiteSpace="nowrap">Kelompok Mandor</Th>
                                    <Th whiteSpace="nowrap">Mandor Panen</Th>
                                    <Th whiteSpace="nowrap">Kerani</Th>
                                    <Th whiteSpace="nowrap">Pemanen / NIP</Th>

                                    <Th
                                        isNumeric
                                        textAlign="right"
                                        whiteSpace="nowrap"
                                        borderLeft="1px solid"
                                        borderColor="blackAlpha.300"
                                        borderBottom="none"
                                    >
                                        Odoo
                                    </Th>
                                    <Th isNumeric textAlign="right" whiteSpace="nowrap">Sakti</Th>
                                    <Th
                                        isNumeric
                                        textAlign="right"
                                        whiteSpace="nowrap"
                                        borderRight="1px solid"
                                        borderColor="blackAlpha.300"
                                        borderBottom="none"
                                    >
                                        Akurasi
                                    </Th>

                                    <Th
                                        isNumeric
                                        textAlign="right"
                                        whiteSpace="nowrap"
                                        borderLeft="1px solid"
                                        borderColor="blackAlpha.300"
                                        borderBottom="none"
                                    >
                                        Odoo
                                    </Th>
                                    <Th isNumeric textAlign="right" whiteSpace="nowrap">Sakti</Th>
                                    <Th
                                        isNumeric
                                        textAlign="right"
                                        whiteSpace="nowrap"
                                        borderRight="1px solid"
                                        borderColor="blackAlpha.300"
                                        borderBottom="none"
                                    >
                                        Akurasi
                                    </Th>

                                    <Th
                                        isNumeric
                                        textAlign="right"
                                        whiteSpace="nowrap"
                                        borderLeft="1px solid"
                                        borderColor="blackAlpha.300"
                                        borderBottom="none"
                                    >
                                        Odoo
                                    </Th>
                                    <Th isNumeric textAlign="right" whiteSpace="nowrap">Sakti</Th>
                                    <Th isNumeric textAlign="right" whiteSpace="nowrap">Akurasi</Th>
                                </Tr>
                            </Thead>

                            <Tbody>
                                {division.foremanGroups.map((fg) => {
                                    const rowKey = `${division.division_id}-${fg.foreman_group_id}`;
                                    const isExpanded = expandedRows[rowKey] ?? false;

                                    return (
                                        <React.Fragment key={rowKey}>
                                            {/* Collapsed / summary row per kemandoran */}
                                            <Tr
                                                bg={isExpanded ? 'green.50' : 'white'}
                                                _hover={{ bg: isExpanded ? 'green.50' : 'gray.50' }}
                                                borderTop="1px solid"
                                                borderColor="blackAlpha.100"
                                            >
                                                <Td>
                                                    <HStack spacing={2}>
                                                        <IconButton
                                                            aria-label="Expand kemandoran"
                                                            icon={isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                                                            size="xs"
                                                            variant="ghost"
                                                            onClick={() => toggleRow(rowKey)}
                                                        />
                                                        <Text fontSize="xs" fontWeight="bold" color="gray.700">
                                                            {fg.foreman_group}
                                                        </Text>
                                                    </HStack>
                                                </Td>
                                                <Td>
                                                    <Text fontSize="xs" color="gray.700">{fg.foreman_name}</Text>
                                                </Td>
                                                {/* Kerani & Pemanen kosong di collapsed row */}
                                                <Td>
                                                    <Text fontSize="2xs" color="gray.400" fontStyle="italic">
                                                        {fg.records.length} pemanen
                                                    </Text>
                                                </Td>
                                                <Td />

                                                {/* HA */}
                                                <Td
                                                    isNumeric
                                                    textAlign="right"
                                                    fontWeight="bold"
                                                    fontSize="xs"
                                                    borderLeft="1px solid"
                                                    borderColor="blackAlpha.200"
                                                >
                                                    {formatNumber(fg.total_odoo_ha)}
                                                </Td>
                                                <Td isNumeric textAlign="right" fontWeight="bold" fontSize="xs">
                                                    {formatNumber(fg.total_sakti_ha)}
                                                </Td>
                                                <Td
                                                    isNumeric
                                                    textAlign="right"
                                                    borderRight="1px solid"
                                                    borderColor="blackAlpha.200"
                                                >
                                                    <AccBadge ratio={fg.ha_acc} />
                                                </Td>

                                                {/* JJG */}
                                                <Td
                                                    isNumeric
                                                    textAlign="right"
                                                    fontWeight="bold"
                                                    fontSize="xs"
                                                    borderLeft="1px solid"
                                                    borderColor="blackAlpha.200"
                                                >
                                                    {formatNumber(fg.total_odoo_jjg, 0)}
                                                </Td>
                                                <Td isNumeric textAlign="right" fontWeight="bold" fontSize="xs">
                                                    {formatNumber(fg.total_sakti_jjg, 0)}
                                                </Td>
                                                <Td
                                                    isNumeric
                                                    textAlign="right"
                                                    borderRight="1px solid"
                                                    borderColor="blackAlpha.200"
                                                >
                                                    <AccBadge ratio={fg.jjg_acc} />
                                                </Td>

                                                {/* BRD */}
                                                <Td
                                                    isNumeric
                                                    textAlign="right"
                                                    fontWeight="bold"
                                                    fontSize="xs"
                                                    borderLeft="1px solid"
                                                    borderColor="blackAlpha.200"
                                                >
                                                    {formatNumber(fg.total_odoo_brd, 0)}
                                                </Td>
                                                <Td isNumeric textAlign="right" fontWeight="bold" fontSize="xs">
                                                    {formatNumber(fg.total_sakti_brd, 0)}
                                                </Td>
                                                <Td isNumeric textAlign="right">
                                                    <AccBadge ratio={fg.brd_acc} />
                                                </Td>
                                            </Tr>

                                            {/* Expanded: individual harvester rows */}
                                            {isExpanded && fg.records.map((record, idx) => (
                                                <Tr
                                                    key={`${rowKey}-${idx}`}
                                                    bg="white"
                                                    _hover={{ bg: 'gray.50' }}
                                                >
                                                    {/* Indent untuk menunjukkan hierarki */}
                                                    <Td pl={10}>
                                                        <Text fontSize="2xs" color="gray.400">—</Text>
                                                    </Td>
                                                    <Td>
                                                        <Text fontSize="xs" color="gray.600">
                                                            {record.foreman_name}
                                                        </Text>
                                                    </Td>
                                                    <Td>
                                                        <Text fontSize="xs" color="gray.700">
                                                            {record.kerani_name}
                                                        </Text>
                                                    </Td>
                                                    <Td>
                                                        <Text fontSize="xs" color="gray.700" fontWeight="bold">
                                                            {record.harvester_name}
                                                        </Text>
                                                        <Text fontSize="2xs" color="gray.500">
                                                            {record.harvester_nip}
                                                        </Text>
                                                    </Td>

                                                    {/* HA */}
                                                    <Td
                                                        isNumeric
                                                        textAlign="right"
                                                        borderLeft="1px solid"
                                                        borderColor="blackAlpha.200"
                                                    >
                                                        <Text fontSize="xs">{formatNumber(record.odoo_ha_qty)}</Text>
                                                    </Td>
                                                    <Td isNumeric textAlign="right">
                                                        <Text fontSize="xs">{formatNumber(record.sakti_ha_qty)}</Text>
                                                    </Td>
                                                    <Td
                                                        isNumeric
                                                        textAlign="right"
                                                        borderRight="1px solid"
                                                        borderColor="blackAlpha.200"
                                                    >
                                                        <AccBadge ratio={record.ha_acc} />
                                                    </Td>

                                                    {/* JJG */}
                                                    <Td
                                                        isNumeric
                                                        textAlign="right"
                                                        borderLeft="1px solid"
                                                        borderColor="blackAlpha.200"
                                                    >
                                                        <Text fontSize="xs">{formatNumber(record.odoo_jjg_qty, 0)}</Text>
                                                    </Td>
                                                    <Td isNumeric textAlign="right">
                                                        <Text fontSize="xs">{formatNumber(record.sakti_jjg_qty, 0)}</Text>
                                                    </Td>
                                                    <Td
                                                        isNumeric
                                                        textAlign="right"
                                                        borderRight="1px solid"
                                                        borderColor="blackAlpha.200"
                                                    >
                                                        <AccBadge ratio={record.jjg_acc} />
                                                    </Td>

                                                    {/* BRD */}
                                                    <Td
                                                        isNumeric
                                                        textAlign="right"
                                                        borderLeft="1px solid"
                                                        borderColor="blackAlpha.200"
                                                    >
                                                        <Text fontSize="xs">{formatNumber(record.odoo_brd_qty, 0)}</Text>
                                                    </Td>
                                                    <Td isNumeric textAlign="right">
                                                        <Text fontSize="xs">{formatNumber(record.sakti_brd_qty, 0)}</Text>
                                                    </Td>
                                                    <Td isNumeric textAlign="right">
                                                        <AccBadge ratio={record.brd_acc} />
                                                    </Td>
                                                </Tr>
                                            ))}
                                        </React.Fragment>
                                    );
                                })}

                                {/* Division subtotal row */}
                                <Tr bg="#e7efdf" borderTop="2px solid" borderColor="blackAlpha.200">
                                    <Td colSpan={4} fontWeight="bold" fontSize="xs">
                                        TOTAL DIVISI
                                    </Td>

                                    <Td
                                        isNumeric
                                        textAlign="right"
                                        fontWeight="bold"
                                        fontSize="xs"
                                        borderLeft="1px solid"
                                        borderColor="blackAlpha.300"
                                    >
                                        {formatNumber(division.total_odoo_ha)}
                                    </Td>
                                    <Td isNumeric textAlign="right" fontWeight="bold" fontSize="xs">
                                        {formatNumber(division.total_sakti_ha)}
                                    </Td>
                                    <Td
                                        isNumeric
                                        textAlign="right"
                                        borderRight="1px solid"
                                        borderColor="blackAlpha.300"
                                    >
                                        <AccBadge ratio={division.ha_acc} />
                                    </Td>

                                    <Td
                                        isNumeric
                                        textAlign="right"
                                        fontWeight="bold"
                                        fontSize="xs"
                                        borderLeft="1px solid"
                                        borderColor="blackAlpha.300"
                                    >
                                        {formatNumber(division.total_odoo_jjg, 0)}
                                    </Td>
                                    <Td isNumeric textAlign="right" fontWeight="bold" fontSize="xs">
                                        {formatNumber(division.total_sakti_jjg, 0)}
                                    </Td>
                                    <Td
                                        isNumeric
                                        textAlign="right"
                                        borderRight="1px solid"
                                        borderColor="blackAlpha.300"
                                    >
                                        <AccBadge ratio={division.jjg_acc} />
                                    </Td>

                                    <Td
                                        isNumeric
                                        textAlign="right"
                                        fontWeight="bold"
                                        fontSize="xs"
                                        borderLeft="1px solid"
                                        borderColor="blackAlpha.300"
                                    >
                                        {formatNumber(division.total_odoo_brd, 0)}
                                    </Td>
                                    <Td isNumeric textAlign="right" fontWeight="bold" fontSize="xs">
                                        {formatNumber(division.total_sakti_brd, 0)}
                                    </Td>
                                    <Td isNumeric textAlign="right">
                                        <AccBadge ratio={division.brd_acc} />
                                    </Td>
                                </Tr>
                            </Tbody>
                        </Table>
                    </Box>
                </Box>
            ))}
        </VStack>
    );
};
